// @refresh skip

import React, { createContext, useContext, useEffect, useState } from 'react'
import {
  supabase,
  UserRow,
  signIn as supabaseSignIn,
  signUp as supabaseSignUp,
  signOutUser,
  sendPasswordResetEmail,
  updatePassword,
  resendEmailVerification,
  updateUserProfile,
  deleteUserAccount,
  isEmailVerified,
  upsertUserProfileDetails,
  uploadUserAvatar,
  updateMFAEnabled,
  ensureUserRowExists,
  clearSupabaseSession,
} from '../lib/supabase'

interface AuthContextValue {
  user: UserRow | null
  loading: boolean
  error: string | null
  isConfigured: boolean
  signIn: (email: string, password: string) => Promise<void>
  signUp: (
    email: string,
    password: string,
    firstName?: string,
    lastName?: string
  ) => Promise<void>
  signOut: () => Promise<void>
  resetPassword: (email: string) => Promise<void>
  updatePassword: (newPassword: string) => Promise<void>
  resendVerification: () => Promise<void>
  updateProfile: (updates: {
    first_name?: string
    last_name?: string
    email?: string
  }) => Promise<void>
  updateProfileDetails: (details: {
    phone_number?: string | null
    date_of_birth?: string | null
    emergency_contact?: string | null
    avatar_url?: string | null
  }) => Promise<void>
  uploadAvatar: (file: File) => Promise<string>
  toggleMFA: (enabled: boolean) => Promise<void>
  deleteAccount: () => Promise<void>
  checkEmailVerified: () => Promise<boolean>
  clearError: () => void
  retryConnection: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined)

// Check if Supabase is properly configured
const checkSupabaseConfiguration = (): { isConfigured: boolean; error?: string } => {
  const url = import.meta.env.VITE_SUPABASE_URL
  const key = import.meta.env.VITE_SUPABASE_ANON_KEY
  
  if (!url || !key) {
    return {
      isConfigured: false,
      error: 'Missing Supabase environment variables. Please create a .env file with VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY.'
    }
  }
  
  if (url.includes('placeholder') || url.includes('your-project') || key.includes('placeholder')) {
    return {
      isConfigured: false,
      error: 'Supabase environment variables contain placeholder values. Please update your .env file with actual credentials.'
    }
  }
  
  return { isConfigured: true }
}

// Timeout wrapper for async operations
const withTimeout = function<T>(promise: Promise<T>, timeoutMs: number = 15000): Promise<T> {
  return Promise.race([
    promise,
    new Promise<T>((_, reject) => 
      setTimeout(() => reject(new Error('Operation timed out')), timeoutMs)
    )
  ])
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<UserRow | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isConfigured, setIsConfigured] = useState(false)

  // Check configuration on mount
  useEffect(() => {
    const config = checkSupabaseConfiguration()
    setIsConfigured(config.isConfigured)
    if (!config.isConfigured) {
      setError(config.error || 'Supabase configuration error')
      setLoading(false)
      return
    }
    
    // If configured, initialize auth
    initializeAuth()
  }, [])

  // Helper to fetch the user profile row from the `users` table
  const fetchUserProfile = async (authUserId: string): Promise<UserRow | null> => {
    try {
      const { data, error } = await withTimeout(
        supabase
          .from('users')
          .select('*')
          .eq('auth_user_id', authUserId)
          .maybeSingle(),
        10000 // 10 second timeout for database queries
      )

      if (error) {
        console.error('Failed to fetch user profile:', error)
        throw error
      }
      
      if (data) return data
      
      // Fallback create row using any stored signup names
      const { data: { user } } = await supabase.auth.getUser()
      const firstName = sessionStorage.getItem('signupFirstName') || ''
      const lastName = sessionStorage.getItem('signupLastName') || ''
      return await ensureUserRowExists({ id: authUserId, email: user?.email }, firstName, lastName)
    } catch (error) {
      console.error('Error fetching user profile:', error)
      
      // If it's a duplicate key error, try to fetch the existing user
      if (error && typeof error === 'object' && 'code' in error && error.code === '23505') {
        console.log('Duplicate user detected, fetching existing user...')
        try {
          const { data: existingData, error: fetchError } = await supabase
            .from('users')
            .select('*')
            .eq('auth_user_id', authUserId)
            .single()
          
          if (fetchError) {
            console.error('Failed to fetch existing user after duplicate error:', fetchError)
            throw error
          }
          
          return existingData
        } catch (fetchErr) {
          console.error('Failed to fetch existing user:', fetchErr)
          throw error
        }
      }
      
      throw error
    }
  }

  const initializeAuth = async () => {
    try {
      setLoading(true)
      setError(null)
      
      // Faster initial load: use getSession (local) before making network call
      const { data: { session } } = await withTimeout(supabase.auth.getSession(), 5000)

      if (session?.user) {
        const profile = await fetchUserProfile(session.user.id)
        setUser(profile)
      }
    } catch (error) {
      console.error('Supabase auth initialization failed:', error)
      
      // Only set error for actual failures, not timeouts during normal operation
      if (error instanceof Error && error.message.includes('timeout')) {
        console.log('Auth initialization timed out, this may be normal during network issues')
        // Don't set error for timeouts - just continue with null user
      } else {
        setError('Failed to initialize authentication. Please check your connection and try again.')
      }
      
      // Try to clear corrupted session only for actual errors
      if (!(error instanceof Error && error.message.includes('timeout'))) {
        try {
          await clearSupabaseSession()
        } catch (clearError) {
          console.error('Failed to clear session:', clearError)
        }
      }
    } finally {
      setLoading(false)
    }
  }

  // Initialize auth state
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        setLoading(true)
        
        // Get initial session
        const { data: { session }, error } = await supabase.auth.getSession()
        
        if (error) {
          console.error('Error getting session:', error)
          setError(error.message)
          setLoading(false)
          return
        }

        if (session?.user) {
          console.log('Auth state changed: SESSION_FOUND', session.user.id)
          const userProfile = await fetchUserProfile(session.user.id)
          setUser(userProfile)
        } else {
          console.log('Auth state changed: NO_SESSION')
        }
        
        setLoading(false)
      } catch (error) {
        console.error('Error initializing auth:', error)
        setError('Failed to initialize authentication')
        setLoading(false)
      }
    }

    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        console.log('Auth state changed:', event, session?.user?.id || 'undefined')
        
        if (event === 'SIGNED_IN' && session?.user) {
          const userProfile = await fetchUserProfile(session.user.id)
          setUser(userProfile)
          setError(null)
        } else if (event === 'SIGNED_OUT') {
          setUser(null)
          setError(null)
        } else if (event === 'TOKEN_REFRESHED' && session?.user) {
          const userProfile = await fetchUserProfile(session.user.id)
          setUser(userProfile)
        }
      }
    )

    initializeAuth()

    return () => {
      subscription.unsubscribe()
    }
  }, [])

  const signIn = async (email: string, password: string) => {
    if (!isConfigured) {
      throw new Error('Authentication not configured. Please check your environment variables.')
    }

    try {
      setError(null)
      await withTimeout(supabaseSignIn(email, password))

      // Immediately fetch and set the user profile so consuming components don't flash back to the login page
      const { data: { user: authUser } } = await supabase.auth.getUser()
      if (authUser) {
        const profile = await fetchUserProfile(authUser.id)
        setUser(profile)
      }
    } catch (error) {
      console.error('Sign in error:', error)
      const errorMessage = (error as Error).message
      if (errorMessage.includes('timeout')) {
        throw new Error('Sign in timed out. Please check your connection and try again.')
      }
      throw error
    }
  }

  const signUp = async (
    email: string,
    password: string,
    firstName = '',
    lastName = ''
  ) => {
    if (!isConfigured) {
      throw new Error('Authentication not configured. Please check your environment variables.')
    }

    try {
      setError(null)
      await withTimeout(supabaseSignUp(email, password, firstName, lastName))
    } catch (error) {
      console.error('Sign up error:', error)
      const errorMessage = (error as Error).message
      if (errorMessage.includes('timeout')) {
        throw new Error('Sign up timed out. Please check your connection and try again.')
      }
      throw error
    }
  }

  const signOut = async () => {
    try {
      setError(null)
      await withTimeout(signOutUser())
      setUser(null)
    } catch (error) {
      console.error('Sign out error:', error)
      // Even if sign out fails, clear local user state
      setUser(null)
    }
  }

  const resetPassword = async (email: string) => {
    if (!isConfigured) {
      throw new Error('Authentication not configured. Please check your environment variables.')
    }

    try {
      setError(null)
      await withTimeout(sendPasswordResetEmail(email))
    } catch (error) {
      console.error('Reset password error:', error)
      throw error
    }
  }

  const updateUserPassword = async (newPassword: string) => {
    try {
      setError(null)
      await withTimeout(updatePassword(newPassword))
    } catch (error) {
      console.error('Update password error:', error)
      throw error
    }
  }

  const resendVerification = async () => {
    try {
      setError(null)
      await withTimeout(resendEmailVerification())
    } catch (error) {
      console.error('Resend verification error:', error)
      throw error
    }
  }

  const updateProfile = async (updates: {
    first_name?: string
    last_name?: string
    email?: string
  }) => {
    try {
      setError(null)
      await withTimeout(updateUserProfile(updates))
      // Refresh user data after update
      const { data: { user: authUser } } = await supabase.auth.getUser()
      if (authUser) {
        const profile = await fetchUserProfile(authUser.id)
        setUser(profile)
      }
    } catch (error) {
      console.error('Update profile error:', error)
      throw error
    }
  }

  const updateProfileDetails = async (details: {
    phone_number?: string | null
    date_of_birth?: string | null
    emergency_contact?: string | null
    avatar_url?: string | null
  }) => {
    try {
      setError(null)
      await withTimeout(upsertUserProfileDetails(details))
    } catch (error) {
      console.error('Update profile details error:', error)
      throw error
    }
  }

  const uploadAvatar = async (file: File): Promise<string> => {
    try {
      setError(null)
      const url = await withTimeout(uploadUserAvatar(file))
      // refresh user profile with new avatar url
      const { data: { user: authUser } } = await supabase.auth.getUser()
      if (authUser) {
        const profile = await fetchUserProfile(authUser.id)
        setUser(profile)
      }
      return url
    } catch (error) {
      console.error('Upload avatar error:', error)
      throw error
    }
  }

  const toggleMFA = async (enabled: boolean) => {
    try {
      setError(null)
      await withTimeout(updateMFAEnabled(enabled))
      const { data: { user: authUser } } = await supabase.auth.getUser()
      if (authUser) {
        const profile = await fetchUserProfile(authUser.id)
        setUser(profile)
      }
    } catch (error) {
      console.error('Toggle MFA error:', error)
      throw error
    }
  }

  const deleteAccount = async () => {
    try {
      setError(null)
      await withTimeout(deleteUserAccount())
      setUser(null)
    } catch (error) {
      console.error('Delete account error:', error)
      throw error
    }
  }

  const checkEmailVerified = async () => {
    try {
      setError(null)
      return await withTimeout(isEmailVerified())
    } catch (error) {
      console.error('Check email verified error:', error)
      throw error
    }
  }

  const clearError = () => {
    setError(null)
  }

  const retryConnection = async () => {
    const config = checkSupabaseConfiguration()
    setIsConfigured(config.isConfigured)
    if (config.isConfigured) {
      await initializeAuth()
    } else {
      setError(config.error || 'Configuration error')
    }
  }

  return (
    <AuthContext.Provider value={{ 
      user, 
      loading, 
      error,
      isConfigured,
      signIn, 
      signUp, 
      signOut, 
      resetPassword,
      updatePassword: updateUserPassword,
      resendVerification,
      updateProfile,
      updateProfileDetails,
      uploadAvatar,
      toggleMFA,
      deleteAccount,
      checkEmailVerified,
      clearError,
      retryConnection
    }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return ctx
}
